// Direct upload fix for comment forms
import { DirectUpload } from "@rails/activestorage"

console.log("[CommentUploadFix] Loading...")

// Wait for DOM to be ready
function ready(fn) {
  if (document.readyState !== 'loading') {
    fn();
  } else {
    document.addEventListener('DOMContentLoaded', fn);
  }
}

// Main setup function
function setupCommentFileUploads() {
  console.log("[CommentUploadFix] Setting up file uploads...")
  
  // Find all file inputs with direct upload
  const setupFileInput = (fileInput) => {
    if (fileInput.__directUploadSetup) return; // Already setup
    fileInput.__directUploadSetup = true;
    
    const form = fileInput.closest('form');
    if (!form) return;

    // Multi-file comment forms use Stimulus comment-attachments instead
    if (form.querySelector('[data-controller~="comment-attachments"]')) return;
    if (fileInput.dataset.commentAttachmentsTarget === "fileInput") return;
    
    console.log("[CommentUploadFix] Found file input in form:", form.id || form.action);
    
    // Override the form's submit event
    const originalSubmit = form.onsubmit;
    let isUploading = false;
    
    form.addEventListener('submit', async function(e) {
      // Skip if no file selected or already uploading
      if (!fileInput.files.length || isUploading) {
        console.log("[CommentUploadFix] No file or already uploading, allowing form submit");
        return;
      }
      
      // Check if we already have a signed ID
      const signedIdField = form.querySelector('input[name="comment[file_signed_id]"]');
      if (signedIdField && signedIdField.value) {
        console.log("[CommentUploadFix] Already have signed ID, allowing form submit");
        return;
      }
      
      // Prevent form submission
      e.preventDefault();
      e.stopPropagation();
      
      console.log("[CommentUploadFix] Intercepted form submit, uploading file first...");
      
      const file = fileInput.files[0];
      const submitButton = form.querySelector('input[type="submit"]');
      
      // Disable submit button
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.value = "Uploading...";
      }
      
      isUploading = true;
      
      // Create direct upload - get URL from Rails-generated data attribute
      const url = fileInput.dataset.directUploadUrl;
      if (!url) {
        console.error("[CommentUploadFix] No direct upload URL found on file input");
        alert("File upload configuration error. Please refresh and try again.");
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.value = "Submit";
        }
        isUploading = false;
        return;
      }
      
      console.log("[CommentUploadFix] Using direct upload URL:", url);
      const upload = new DirectUpload(file, url);
      
      // In development, fix the URL issue
      if (window.location.hostname === 'localhost' && window.location.port === '4000') {
        const originalXHROpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function(method, url, ...args) {
          if (url && url.includes('https://localhost/rails/active_storage/')) {
            url = url.replace('https://localhost/', 'http://localhost:4000/');
            console.log("[CommentUploadFix] Fixed blob storage URL:", url);
          }
          return originalXHROpen.call(this, method, url, ...args);
        };
        
        // Restore after upload
        setTimeout(() => {
          XMLHttpRequest.prototype.open = originalXHROpen;
        }, 30000); // 30 seconds should be enough
      }
      
      upload.create((error, blob) => {
        isUploading = false;
        
        // Get submit button reference here since it might have changed
        const currentSubmitButton = form.querySelector('input[type="submit"]');
        
        if (error) {
          console.error("[CommentUploadFix] Upload failed:", error);
          alert(`Upload failed: ${error}`);
          
          if (currentSubmitButton) {
            currentSubmitButton.disabled = false;
            currentSubmitButton.value = "Submit";
          }
        } else {
          console.log("[CommentUploadFix] Upload successful, blob:", blob);
          console.log("[CommentUploadFix] Blob details:", {
            id: blob.id,
            signed_id: blob.signed_id,
            filename: blob.filename,
            content_type: blob.content_type,
            byte_size: blob.byte_size
          });
          
          // Create or update the hidden signed ID field
          let signedIdField = form.querySelector('input[name="comment[file_signed_id]"]');
          if (!signedIdField) {
            signedIdField = document.createElement('input');
            signedIdField.type = 'hidden';
            signedIdField.name = 'comment[file_signed_id]';
            form.appendChild(signedIdField);
          }
          signedIdField.value = blob.signed_id;
          
          // Log to verify the field was set
          console.log("[CommentUploadFix] Set signed_id field value:", signedIdField.value);
          
          // Clear the file input
          fileInput.value = '';
          
          // Re-enable submit button
          if (currentSubmitButton) {
            currentSubmitButton.disabled = false;
            currentSubmitButton.value = "Submit";
          }
          
          console.log("[CommentUploadFix] Submitting form with signed_id:", blob.signed_id);
          
          // Submit the form - trigger the submit event for AJAX handlers
          console.log("[CommentUploadFix] Triggering form submit...");
          
          // For item comment forms that use custom AJAX submission
          if (currentSubmitButton && form.id === 'new_comment') {
            console.log("[CommentUploadFix] Clicking submit button for AJAX form");
            currentSubmitButton.click();
          } else {
            // For other forms
            const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
            form.dispatchEvent(submitEvent);
          }
        }
      });
    }, true); // Use capture phase to intercept before other handlers
  };
  
  // Setup all existing file inputs - Rails adds data-direct-upload-url automatically
  document.querySelectorAll('input[type="file"][data-direct-upload-url], input[type="file"][direct-upload="true"]').forEach(setupFileInput);
  
  // Watch for new file inputs (for dynamically loaded forms)
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) { // Element node
          // Look for file inputs with direct upload
          if (node.matches && (node.matches('input[type="file"][data-direct-upload-url]') || node.matches('input[type="file"][direct-upload="true"]'))) {
            console.log("[CommentUploadFix] Found new file input via mutation observer");
            setupFileInput(node);
          } else if (node.querySelectorAll) {
            const inputs = node.querySelectorAll('input[type="file"][data-direct-upload-url], input[type="file"][direct-upload="true"]');
            if (inputs.length > 0) {
              console.log("[CommentUploadFix] Found", inputs.length, "new file inputs via mutation observer");
              inputs.forEach(setupFileInput);
            }
          }
        }
      });
    });
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  console.log("[CommentUploadFix] Setup complete, watching for new forms...");
}

// Run on various events to catch dynamically loaded content
ready(setupCommentFileUploads);
document.addEventListener('turbo:load', setupCommentFileUploads);
document.addEventListener('turbo:frame-load', setupCommentFileUploads);
document.addEventListener('turbo:render', setupCommentFileUploads);

// Export for debugging
window.__debugCommentUploads = {
  findForms: () => document.querySelectorAll('form[id*="comment"]'),
  findFileInputs: () => document.querySelectorAll('input[type="file"]'),
  checkFileInput: () => {
    const inputs = document.querySelectorAll('input[type="file"]');
    inputs.forEach((input, i) => {
      console.log(`File input ${i}:`, {
        hasDirectUploadUrl: !!input.dataset.directUploadUrl,
        directUploadUrl: input.dataset.directUploadUrl,
        hasDirectUploadAttr: input.hasAttribute('direct-upload'),
        isSetup: !!input.__directUploadSetup
      });
    });
  },
  setupAll: setupCommentFileUploads
};